import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ArrowRight, Trash2, ShoppingBag, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
    const { items, remove, clear } = useCart();
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    useEffect(() => {
        api.get("/courses").then(({ data }) => {
            setCatalog(data.courses || []);
        }).finally(() => setLoading(false));
    }, []);

    const rows = useMemo(() => {
        const byId = Object.fromEntries(catalog.map((c) => [c.id, c]));
        return items.map((id) => byId[id]).filter(Boolean);
    }, [items, catalog]);

    const subtotal = useMemo(() => rows.reduce((s, r) => s + Number(r.price || 0), 0), [rows]);

    const handleCheckout = async () => {
        if (rows.length === 0) return;
        setCheckoutLoading(true);
        try {
            const { data } = await api.post("/checkout/cart", {
                module_ids: rows.map((r) => r.id),
                origin_url: window.location.origin,
            });
            if (data?.url) {
                window.location.href = data.url;
                return;
            }
            toast.error("Checkout failed to start");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start checkout");
        } finally {
            setCheckoutLoading(false);
        }
    };

    if (loading) {
        return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500" data-testid="cart-loading">Loading cart…</div>;
    }

    if (rows.length === 0) {
        return (
            <div className="container-edge py-24" data-testid="cart-empty">
                <div className="max-w-lg">
                    <p className="overline">Cart</p>
                    <h1 className="mt-3 font-heading font-black text-5xl tracking-tighter leading-[0.95]">Your cart is empty.</h1>
                    <p className="mt-4 text-muted-foreground">Pick the modules that fit your teaching first. You can always come back to check out.</p>
                    <Link
                        to="/courses"
                        data-testid="cart-empty-browse"
                        className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors"
                    >
                        Browse modules <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="container-edge py-16" data-testid="cart-page">
            <div className="mb-6">
                <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900" data-testid="cart-back-courses">
                    <ArrowLeft size={14} /> Continue shopping
                </Link>
            </div>

            <header className="mb-10 flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="overline">Cart</p>
                    <h1 className="mt-3 font-heading font-black text-5xl tracking-tighter leading-[0.95]">
                        {rows.length} module{rows.length === 1 ? "" : "s"} ready.
                    </h1>
                    <p className="mt-3 text-muted-foreground max-w-xl">One payment. Instant unlock in your dashboard. Bundles and school licenses have their own pricing pages.</p>
                </div>
                <button
                    onClick={() => { clear(); toast.message("Cart cleared"); }}
                    data-testid="cart-clear"
                    className="text-sm font-medium text-neutral-500 hover:text-red-700 underline underline-offset-4"
                >
                    Clear cart
                </button>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                <div className="lg:col-span-2" data-testid="cart-list">
                    <div className="border border-black/10">
                        {rows.map((r, i) => (
                            <div
                                key={r.id}
                                data-testid={`cart-row-${r.id}`}
                                className={`p-6 flex items-start justify-between gap-6 bg-white ${i < rows.length - 1 ? "border-b border-black/10" : ""}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono text-xs tracking-widest text-klein">MOD / {r.number}</span>
                                        <span className="text-xs uppercase tracking-widest opacity-60">{r.duration}</span>
                                    </div>
                                    <h3 className="mt-2 font-heading font-bold text-xl tracking-tight leading-tight">{r.title}</h3>
                                    <p className="mt-1 text-xs uppercase tracking-widest text-klein">{r.tagline}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-heading font-black text-2xl">${r.price}</div>
                                    <button
                                        onClick={() => { remove(r.id); toast.message(`Removed ${r.title}`); }}
                                        data-testid={`cart-remove-${r.id}`}
                                        className="mt-2 inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-red-700"
                                    >
                                        <Trash2 size={12} /> Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <aside data-testid="cart-summary" className="border border-black/10 bg-white p-8 h-fit sticky top-24">
                    <p className="overline">Summary</p>
                    <div className="mt-4 flex items-baseline justify-between">
                        <span className="text-sm text-muted-foreground">Subtotal</span>
                        <span data-testid="cart-subtotal" className="font-heading font-black text-3xl">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">Taxes calculated at Stripe if applicable. No hidden fees.</div>

                    <button
                        onClick={handleCheckout}
                        disabled={checkoutLoading || rows.length === 0}
                        data-testid="cart-checkout-btn"
                        className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ShoppingBag size={16} /> {checkoutLoading ? "Redirecting to Stripe…" : `Checkout · $${subtotal.toFixed(2)}`}
                    </button>

                    <p className="mt-4 text-xs text-neutral-500">
                        Prefer a single-module Buy now? Head back to the <Link to="/courses" className="underline">Courses page</Link>.
                    </p>
                </aside>
            </div>
        </div>
    );
}
